import { api } from "../base";
import type {
  IUnsettledTradesResponse,
  IAuditEventsResponse,
  IAssetMultiplier,
  IAssetMultiplierList,
  IMarkLiveResponse,
  IAccountRiskLimit,
  IAccountRiskLimitList,
  IAccountRiskLimitUpdate,
  AuditEventKind,
  AuditLeg,
  AuditOutcome,
} from "@pulse-ui/codegen-v2/generated_ts/risk_projection";
import type { IRiskExposureResponse } from "@pulse-ui/codegen-v2/generated_ts/risk_checks";

export type {
  IBuyingPowerResponse,
  IBuyingPowerAsset,
  IUnsettledTradesResponse,
  IUnsettledTradeLot,
  IAuditEvent,
  IAuditEventsResponse,
  IAssetMultiplier,
  IAssetMultiplierList,
  IMarkLiveResponse,
  IAccountRiskLimit,
  IAccountRiskLimitList,
  IAccountRiskLimitUpdate,
} from "@pulse-ui/codegen-v2/generated_ts/risk_projection";

export {
  AuditEventKind,
  AuditLeg,
  AuditOutcome,
  AuditRiskMode,
  HaltExemptReason,
  PnlLossLimitState,
} from "@pulse-ui/codegen-v2/generated_ts/risk_projection";

export {
  ExposurePriceStatus,
  AssetMidSource,
  CreditLineState,
  type IAssetExposure,
  type ICounterpartyExposure,
  type ITouchExposureView,
  type IRiskExposureResponse,
} from "@pulse-ui/codegen-v2/generated_ts/risk_checks";

export interface GetDecisionAuditParams {
  account?: string;
  app?: string;
  event_kind?: AuditEventKind;
  outcome?: AuditOutcome;
  leg?: AuditLeg;
  base_asset?: string;
  trade_id?: string;
  hold_id?: string;
  from?: string;
  to?: string;
  limit?: number;
  cursor?: string;
}

export interface GetRiskExposureParams {
  account?: string;
}

const ACCOUNT_RISK_LIMITS_URL = "/api/risk/v1/account-risk-limits";

export const riskEndpoints = api.injectEndpoints({
  endpoints: (build) => ({
    getRiskExposure: build.query<IRiskExposureResponse, GetRiskExposureParams | void>({
      query: (params) => ({
        url: "/api/risk/v1/risk-exposure",
        params: params?.account ? { account: params.account } : undefined,
      }),
      providesTags: ["RiskExposure"],
    }),
    getUnsettledTrades: build.query<IUnsettledTradesResponse, string>({
      query: (account) => ({
        url: "/api/risk/v1/unsettled-trades",
        params: { account },
      }),
      providesTags: ["UnsettledTrades"],
    }),
    getDecisionAudit: build.query<IAuditEventsResponse, GetDecisionAuditParams>({
      query: (params) => ({
        url: "/api/risk/v1/decision-audit",
        params,
      }),
      providesTags: ["AuditEvents"],
    }),
    getMultipliers: build.query<IAssetMultiplierList, void>({
      query: () => ({ url: "/api/risk/v1/multipliers" }),
      providesTags: ["Multipliers"],
    }),
    putMultiplier: build.mutation<IAssetMultiplier, IAssetMultiplier>({
      query: (multiplier) => ({
        url: "/api/risk/v1/multipliers",
        method: "PUT",
        body: multiplier,
      }),
      invalidatesTags: ["Multipliers"],
    }),
    deleteMultiplier: build.mutation<void, string>({
      query: (asset) => ({
        url: "/api/risk/v1/multipliers",
        method: "DELETE",
        params: { asset },
      }),
      invalidatesTags: ["Multipliers"],
    }),
    // Clears an account quarantine. The live buying-power WS republishes the
    // account with a cleared stale flag, so no RTK cache invalidation is needed.
    markAccountLive: build.mutation<IMarkLiveResponse, string>({
      query: (account) => ({
        url: "/api/risk/v1/accounts/mark-live",
        method: "POST",
        params: { account, confirm: true },
      }),
    }),
    // Edits one account's credit/loss limits independently; the same live
    // buying-power WS republish covers the response, so no cache invalidation.
    putAccountRiskLimit: build.mutation<IAccountRiskLimit, IAccountRiskLimitUpdate>({
      query: (update) => ({
        url: ACCOUNT_RISK_LIMITS_URL,
        method: "PUT",
        body: update,
      }),
    }),
    // The authoritative row for one account; queried on demand rather than
    // subscribed, so this is exposed lazily, not as an always-warm cache hook.
    getAccountRiskLimits: build.query<IAccountRiskLimitList, string>({
      query: (account) => ({
        url: ACCOUNT_RISK_LIMITS_URL,
        params: { account },
      }),
    }),
  }),
});

export const {
  useGetRiskExposureQuery,
  useGetUnsettledTradesQuery,
  useLazyGetUnsettledTradesQuery,
  useGetDecisionAuditQuery,
  useLazyGetDecisionAuditQuery,
  useGetMultipliersQuery,
  usePutMultiplierMutation,
  useDeleteMultiplierMutation,
  useMarkAccountLiveMutation,
  usePutAccountRiskLimitMutation,
  useLazyGetAccountRiskLimitsQuery,
} = riskEndpoints;
